import { useRef, useState, useEffect } from 'react'
import { langSpeedChart } from '../assets/langSpeedChart'
import { useTrkptsWS, useLang } from '../utils/hooks'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import {
  SpeedHighchartsContainer,
  ApplyContainer,
  SlidersContainer,
  SlidersAndButtonsContainer,
} from './StyledElements'
import XSlider from './XSlider'
import SlidersGroup from './SlidersGroup'

import { cloneDeep, find } from 'lodash'

import { five_min, ten_min } from '../assets/assets_functions'

require('highcharts/modules/annotations')(Highcharts)

const SpeedChart = ({
  origin,
  loading,
  options,
  timeDomain,
  handleUpdateMapMarkers,
  handleApplySelection,
  handleSeeStats,
  handleCancel,
  handleSave,
  disabledDemo,
}) => {
  const { trkptsWS } = useTrkptsWS()
  const { lang } = useLang()
  const choosenLang = lang === 'fr' ? langSpeedChart.fr : langSpeedChart.eng

  const [sliders, setSliders] = useState([])
  const [xSliderValue, setXSliderValue] = useState()
  const [applyDone, setApplyDone] = useState(false)

  const chart = useRef(null)

  useEffect(() => {
    setXSliderValue((timeDomain[0] + timeDomain[1]) / 2)
    if (applyDone === true) return

    let sliders = []
    let open_slider = -1
    let close_slider = -1
    trkptsWS.brut.forEach((trkpt) => {
      if (trkpt.excluded === 1) {
        if (open_slider === -1) {
          open_slider = trkpt.timeU
        }
        close_slider = trkpt.timeU
      } else {
        if (open_slider !== -1) {
          sliders.push([open_slider, close_slider])
          open_slider = -1
        }
      }
    })
    if (open_slider !== -1) sliders.push([open_slider, close_slider])
    setSliders(sliders)
    handleUpdateMapMarkers(sliders)
  }, [timeDomain, trkptsWS.brut.length])

  const handleSliders = (values, index) => {
    const new_values = cloneDeep(sliders)
    new_values[index] = values
    setSliders(new_values)
    handleUpdateMapMarkers(new_values)
  }

  const handleSliderUpdate = (value) => {
    const temp_value = Math.round(value / 1000) * 1000
    /* console.log('speed :' + temp_value) */
    const temp_point = find(
      chart.current.chart.series[0].points,
      (point) => point.x === temp_value
    )

    if (temp_point) {
      /* console.log('x :' + temp_point.x) */
      chart.current.chart.xAxis[0].drawCrosshair(null, temp_point)
    } else {
      console.log(temp_value + '   nopoint')
    }
  }

  const handleAddSlider = (x) => {
    const new_values = cloneDeep(sliders)
    const min = timeDomain[0]
    const max = timeDomain[1]
    const low_val = x - five_min
    const high_val = x + five_min
    let new_slider_values
    if (low_val >= min && high_val <= max) {
      new_slider_values = [low_val, high_val]
    } else if (low_val < min) {
      new_slider_values = [min, min + ten_min]
    } else {
      new_slider_values = [max - ten_min, max]
    }
    new_values.push(new_slider_values)
    setSliders(new_values)
    handleUpdateMapMarkers(new_values)
  }

  const handleDelSlider = (index) => {
    const new_values = cloneDeep(sliders)
    new_values.splice(index, 1)
    setSliders(new_values)
    handleUpdateMapMarkers(new_values)
  }

  const handleXSliderValue = (value) => {
    setXSliderValue(value)
  }

  const onApplySelection = () => {
    setSliders([])
    setApplyDone(true)
    handleApplySelection(sliders)
  }

  const onSeeStats = () => {
    setApplyDone(false)
    handleSeeStats()
  }

  const onCancel = () => {
    setApplyDone(false)
    handleCancel()
  }

  const onSave = () => {
    handleSave(sliders)
  }

  return (
    <>
      <SpeedHighchartsContainer id="complete_speed_gps_chart_div">
        <HighchartsReact
          ref={chart}
          highcharts={Highcharts}
          options={options}
        />
      </SpeedHighchartsContainer>
      <SlidersAndButtonsContainer
        chart={chart}
        id="SlidersAndButtonsContainer"
        className="block"
      >
        <SlidersContainer chart={chart} id="slidersContainer" className="block">
          <XSlider
            timeDomain={timeDomain}
            handleValue={[xSliderValue]}
            handleAddSlider={handleAddSlider}
            bDisabled={sliders.length === 4}
            handleXSliderValue={handleXSliderValue}
            chart={chart}
          />
          {chart.current && (
            <SlidersGroup
              id="SlidersGroup"
              sliders={sliders}
              domain={timeDomain}
              handleSliderChange={handleSliders}
              handleDelSlider={handleDelSlider}
              handleSliderUpdate={handleSliderUpdate}
              chart={chart}
            />
          )}
        </SlidersContainer>
      </SlidersAndButtonsContainer>
      <ApplyContainer>
        <div className="level">
          <div className="level-left">
            <div className="level-item"></div>
          </div>
          <div className="level-center">
            <div className="level-item"></div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <div className="buttons are-small">
                <div className="field is-grouped">
                  {sliders.length > 0 && origin !== 'modify' && (
                    <button
                      className="button is-primary is-small"
                      onClick={onApplySelection}
                    >
                      {choosenLang.apply}
                    </button>
                  )}
                  {origin !== 'modify' && (
                    <button
                      className="button is-primary is-small"
                      disabled={!applyDone}
                      onClick={onCancel}
                    >
                      {choosenLang.cancelAll}
                    </button>
                  )}
                  <button
                    className={
                      'button is-primary is-normal' +
                      (loading ? 'is-loading' : '')
                    }
                    onClick={origin === 'try' ? onSeeStats : onSave}
                    disabled={disabledDemo}
                    title={disabledDemo ? choosenLang.registeredOnly : ''}
                  >
                    {origin === 'try' ? choosenLang.see : choosenLang.save}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ApplyContainer>
    </>
  )
}

export default SpeedChart
